import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const { FiSearch, FiEye } = FiIcons;

const ContactMessages = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [selectedMessage, setSelectedMessage] = useState(null);

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async () => {
        try {
            const { data } = await api.get('/contacts');
            setMessages(data);
        } catch (error) {
            console.error(error);
            toast.error('Failed to load contact messages');
        } finally {
            setLoading(false);
        }
    };

    const openMessage = async (msg) => {
        setSelectedMessage(msg);

        if (msg.status === 'new') {
            await api.patch(`/contacts/${msg.id}/read`);
            fetchMessages(); // refresh list
        }
    };

    const filteredMessages = messages.filter(msg => {
        const matchesSearch =
            msg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            msg.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            msg.subject.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus =
            filterStatus === 'all' || msg.status === filterStatus;

        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return <p className="text-gray-500">Loading messages...</p>;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        Contact Messages
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Messages sent from the contact form
                    </p>
                </div>
            </div>

            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <SafeIcon
                        icon={FiSearch}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4"
                    />
                    <input
                        type="text"
                        placeholder="Search messages..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-emerald-500"
                    />
                </div>

                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                    <option value="all">All Status</option>
                    <option value="new">New</option>
                    <option value="read">Read</option>
                </select>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                    Sender
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                    Subject
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                    Date
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                    Actions
                                </th>
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredMessages.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-6 py-6 text-center text-gray-500">
                                        No messages found
                                    </td>
                                </tr>
                            )}

                            {filteredMessages.map((msg, index) => (
                                <motion.tr
                                    key={msg.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.04 }}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    <td className="px-6 py-4">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-semibold">
                                                {msg.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {msg.name}
                                                </div>
                                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                                    {msg.email}
                                                </div>
                                            </div>
                                        </div>
                                    </td>

                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                                        {msg.subject}
                                    </td>

                                    <td className="px-6 py-4">
                                        <span
                                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                msg.status === 'new'
                                                    ? 'bg-emerald-100 text-emerald-700'
                                                    : 'bg-gray-200 text-gray-700'
                                            }`}
                                        >
                                            {msg.status}
                                        </span>
                                    </td>

                                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                        {new Date(msg.created_at).toLocaleDateString()}
                                    </td>

                                    <td className="px-6 py-4 text-right">
                                        <button
                                            onClick={() => openMessage(msg)}
                                            className="text-emerald-600 hover:text-emerald-800 dark:hover:text-emerald-400"
                                            title="View message"
                                        >
                                            <SafeIcon icon={FiEye} className="w-4 h-4" />
                                        </button>
                                    </td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {selectedMessage && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl p-6"
                        >
                            {/* Header */}
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Contact Message Details
                                </h3>
                                <button
                                    onClick={() => setSelectedMessage(null)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    ✕
                                </button>
                            </div>

                            {/* Details */}
                            <div className="space-y-4 text-sm">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-gray-500">Name</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {selectedMessage.name}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-gray-500">Email</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {selectedMessage.email}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-gray-500">Subject</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {selectedMessage.subject}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-gray-500">Status</p>
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                            selectedMessage.status === 'new'
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : selectedMessage.status === 'replied'
                                                    ? 'bg-blue-100 text-blue-700'
                                                    : 'bg-gray-200 text-gray-700'
                                        }`}>
                                            {selectedMessage.status}
                                        </span>
                                    </div>
                                </div>

                                {/* Message */}
                                <div>
                                    <p className="text-gray-500 mb-1">Message</p>
                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 whitespace-pre-wrap text-gray-900 dark:text-gray-100">
                                        {selectedMessage.message}
                                    </div>
                                </div>

                                {/* Meta */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-gray-500">
                                    <div>
                                        <p>IP Address</p>
                                        <p className="font-medium text-gray-700 dark:text-gray-300">
                                            {selectedMessage.ip_address || 'N/A'}
                                        </p>
                                    </div>

                                    <div>
                                        <p>User Agent</p>
                                        <p className="font-medium text-gray-700 dark:text-gray-300 break-all">
                                            {selectedMessage.user_agent || 'N/A'}
                                        </p>
                                    </div>

                                    <div>
                                        <p>Created At</p>
                                        <p className="font-medium">
                                            {new Date(selectedMessage.created_at).toLocaleString()}
                                        </p>
                                    </div>

                                    <div>
                                        <p>Last Updated</p>
                                        <p className="font-medium">
                                            {new Date(selectedMessage.updated_at).toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end mt-6">
                                <button
                                    onClick={() => setSelectedMessage(null)}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Close
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContactMessages;
